import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { getClientId } from "../lib/clientId";
import { getWatchlistItems, addWatchlistItem, removeWatchlistItem, trackEvent } from "../lib/api";

const WatchlistContext = createContext(null);
const KEY = "memeradar_watchlist";

export const WatchlistProvider = ({ children }) => {
  const [watchlist, setWatchlist] = useState([]);
  const { user, requireAuth } = useAuth();

  // Load: for logged-in users the account (server) is the source of truth and syncs
  // across devices; any coins saved locally while logged out are migrated once.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (user?.id) {
        const cid = getClientId();
        try {
          const { addresses } = await getWatchlistItems(cid);
          let merged = addresses || [];
          const local = JSON.parse(localStorage.getItem(KEY) || "[]");
          const toMigrate = local.filter((a) => !merged.includes(a));
          for (const a of toMigrate) {
            const res = await addWatchlistItem(cid, a);
            merged = res.addresses;
          }
          if (toMigrate.length) localStorage.removeItem(KEY);
          if (!cancelled) setWatchlist(merged);
        } catch {
          if (!cancelled) setWatchlist(JSON.parse(localStorage.getItem(KEY) || "[]"));
        }
      } else {
        setWatchlist(JSON.parse(localStorage.getItem(KEY) || "[]"));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.id]);

  const toggle = useCallback((address) => {
    if (!requireAuth("save coins to your watchlist")) return;
    const cid = getClientId();
    setWatchlist((prev) => {
      const has = prev.includes(address);
      const next = has ? prev.filter((a) => a !== address) : [...prev, address];
      (has ? removeWatchlistItem(cid, address) : addWatchlistItem(cid, address)).catch(() => {});
      if (!has) trackEvent("watchlist_add", cid, { mint: address });
      return next;
    });
  }, [requireAuth]);

  const isWatched = useCallback((address) => watchlist.includes(address), [watchlist]);

  return (
    <WatchlistContext.Provider value={{ watchlist, toggle, isWatched }}>
      {children}
    </WatchlistContext.Provider>
  );
};

export const useWatchlist = () => useContext(WatchlistContext);
